import { Settings } from '@/settings';
import {
  ITransactionsState,
  TSetServiceType,
  TSetUsages,
  TSetFrom,
  TSetTo,
  TLoadUsages,
  TSetSubscriptionId,
  ITransactionsStore,
  AggregateUsagePeriodType,
  ISubscriptionUsage,
  IServerSubscriptionUsageResponse,
  IServerSubscriptionUsageRequest,
  TSetTotalUsagesDisplay,
  TSetDailyDataPointsUnitsDisplay,
  TSetMonthlyDataPointsUnitsDisplay,
  TSetTranscriptionDailyDividend,
  TSetTranscriptionMonthlyDividend
} from '@/store/contracts/subscriptions/transactions';
import { chainHandlers, DefaultIStatusHandler, handleErrorStatusResult, IStatusHandler } from '@/store/utils/apiResponseHandling';
import { sendRequest } from '@/store/utils/axiosUtils';
import { toApp as usageToApp } from '@/store/subscriptions/mapping/subscriptionUsage';
import { inject, provide, reactive, readonly } from 'vue';
import { store as validationStore } from '@/store/validation';
import filterValidationModel from '@/validation/transactionsFilterValidationModel';
import { SubscriptionServiceType } from '@/store/contracts/subscriptions/subscriptionServiceType';

const TransactionsStoreKey = Symbol('TransactionsStore');

function setSubscriptionId (state: ITransactionsState): TSetSubscriptionId {
  return (subscriptionId) => {
    state.subscriptionId = subscriptionId;
    state.dailyUsages = [];
    state.monthlyUsages = [];
    state.filterModel = {
      serviceType: null,
      from: null,
      to: null
    };
  };
}

function setUsages (state: ITransactionsState): TSetUsages {
  return (dailyUsages, monthlyUsages) => {
    state.dailyUsages = dailyUsages;
    state.monthlyUsages = monthlyUsages;
  };
}

function formatTotalUsage (totalUsage: number): string {
  const hours = Math.floor(totalUsage / 3600);
  const minutes = Math.floor((totalUsage % 3600) / 60);
  const seconds = totalUsage % 60;

  let result = '';
  if (hours > 0) { result += `${hours} Hours, `; }
  if (minutes > 0) { result += `${minutes} Minutes, `; }
  if (seconds > 0 || result === '') { result += `${seconds} Seconds`; }
  return result;
}

function setTotalUsagesDisplay (state: ITransactionsState): TSetTotalUsagesDisplay {
  return (totalUsagesDisplay) => {
    state.totalUsagesDisplay = totalUsagesDisplay;
  };
}

function setDailyDataPointsUnitsDisplay (state: ITransactionsState): TSetDailyDataPointsUnitsDisplay {
  return (dailyDataPointsUnitsDisplay) => {
    state.dailyDataPointsUnitsDisplay = dailyDataPointsUnitsDisplay;
  };
}

function setMonthlyDataPointsUnitsDisplay (state: ITransactionsState): TSetMonthlyDataPointsUnitsDisplay {
  return (monthlyDataPointsUnitsDisplay) => {
    state.monthlyDataPointsUnitsDisplay = monthlyDataPointsUnitsDisplay;
  };
}

function setServiceType (state: ITransactionsState): TSetServiceType {
  return (serviceType) => {
    state.filterModel.serviceType = serviceType;
  };
}

function setFrom (state: ITransactionsState): TSetFrom {
  return (from) => {
    state.filterModel.from = from;
  };
}

function setTo (state: ITransactionsState): TSetTo {
  return (to) => {
    state.filterModel.to = to;
  };
}

function setTranscriptionDailyDividend (state: ITransactionsState): TSetTranscriptionDailyDividend {
  return (transcriptionDailyDividend) => {
    state.transcriptionDailyDividend = transcriptionDailyDividend;
  };
}

function setTranscriptionMonthlyDividend (state: ITransactionsState): TSetTranscriptionMonthlyDividend {
  return (transcriptionMonthlyDividend) => {
    state.transcriptionMonthlyDividend = transcriptionMonthlyDividend;
  };
}

function fetchUsagesForAggregate (
  state: ITransactionsState,
  aggregateType: AggregateUsagePeriodType,
  handler?: Partial<IStatusHandler<IServerSubscriptionUsageResponse>>
): Promise<[boolean, ISubscriptionUsage[]]> {
  return new Promise<[boolean, ISubscriptionUsage[]]>((resolve) => {
    sendRequest<IServerSubscriptionUsageResponse>(
      `${Settings.adminApiBaseUrl}/admin/v1/subscription/usage/load`,
      'post',
      {
        SubscriptionId: state.subscriptionId,
        To: state.filterModel.to,
        From: state.filterModel.from,
        AggregateType: aggregateType,
        ServiceType: SubscriptionServiceType[state.filterModel.serviceType ?? SubscriptionServiceType.Vision]
      } as IServerSubscriptionUsageRequest
    ).then((response) => handleErrorStatusResult(response, chainHandlers({
      onSuccess: async (u) => resolve([true, u.Usages.map(usageToApp)]),
      onError: async (err) => {
        await DefaultIStatusHandler.onError(err);
        resolve([false, []]);
      },
      onNotAuthenticated: async (err) => {
        await DefaultIStatusHandler.onNotAuthenticated(err);
        resolve([false, []]);
      },
      onNotFound: async (err) => {
        await DefaultIStatusHandler.onNotFound(err);
        resolve([false, []]);
      }
    }, handler)));
  });
}

function loadUsages (state: ITransactionsState): TLoadUsages {
  return async (handler) => {
    const isValid = await validationStore.doValidation({
      model: filterValidationModel,
      value: state.filterModel
    });
    if (state.subscriptionId && isValid) {
      const results = await Promise.all([
        fetchUsagesForAggregate(state, AggregateUsagePeriodType.Daily, handler),
        fetchUsagesForAggregate(state, AggregateUsagePeriodType.Monthly, handler)
      ]);

      if (results.none(result => !result[0])) {
        const dailyUsages = results[0][1];
        const monthlyUsages = results[1][1];
        const totalUsages = dailyUsages.length > 0 ? dailyUsages.map(a => a.transactionCount).reduce((a, b) => a + b) : 0;
        const serviceType = state.filterModel.serviceType;
        const maxDailyUsage = Math.max(...dailyUsages.map(usage => usage.transactionCount));
        const maxMonthlyUsage = Math.max(...monthlyUsages.map(usage => usage.transactionCount));

        let dailyTranscriptionDividend = 1;
        let monthlyTranscriptionDividend = 1;
        let transcriptionDailyLabel = 'Seconds';
        let transcriptionMonthlyLabel = 'Seconds';

        if (serviceType === SubscriptionServiceType.Transcription) {
          // Set Daily Dividend
          if (maxDailyUsage >= 86400) {
            dailyTranscriptionDividend = 86400;
            transcriptionDailyLabel = 'Days';
            setTranscriptionDailyDividend(state)(dailyTranscriptionDividend);
          } else if (maxDailyUsage >= 3600) {
            dailyTranscriptionDividend = 3600;
            transcriptionDailyLabel = 'Hours';
            setTranscriptionDailyDividend(state)(dailyTranscriptionDividend);
          } else if (maxDailyUsage >= 60) {
            dailyTranscriptionDividend = 60;
            transcriptionDailyLabel = 'Minutes';
            setTranscriptionDailyDividend(state)(dailyTranscriptionDividend);
          }

          // Set Monthly Dividend
          if (maxMonthlyUsage >= 86400) {
            monthlyTranscriptionDividend = 86400;
            transcriptionMonthlyLabel = 'Days';
            setTranscriptionMonthlyDividend(state)(monthlyTranscriptionDividend);
          } else if (maxMonthlyUsage >= 3600) {
            monthlyTranscriptionDividend = 3600;
            transcriptionMonthlyLabel = 'Hours';
            setTranscriptionMonthlyDividend(state)(monthlyTranscriptionDividend);
          } else if (maxMonthlyUsage >= 60) {
            monthlyTranscriptionDividend = 60;
            transcriptionMonthlyLabel = 'Minutes';
            setTranscriptionMonthlyDividend(state)(monthlyTranscriptionDividend);
          }
        } else {
          setTranscriptionDailyDividend(state)(undefined);
          setTranscriptionMonthlyDividend(state)(undefined);
        }

        for (const usage of dailyUsages) {
          if (usage.serviceType === SubscriptionServiceType.Transcription) {
            usage.transactionCountNormalized = usage.transactionCount / dailyTranscriptionDividend;
          } else {
            usage.transactionCountNormalized = usage.transactionCount;
          }
        }

        for (const usage of monthlyUsages) {
          if (usage.serviceType === SubscriptionServiceType.Transcription) {
            usage.transactionCountNormalized = usage.transactionCount / monthlyTranscriptionDividend;
          } else {
            usage.transactionCountNormalized = usage.transactionCount;
          }
        }

        setUsages(state)(dailyUsages, monthlyUsages);

        if (serviceType === SubscriptionServiceType.Profile) {
          setDailyDataPointsUnitsDisplay(state)('Searches');
          setMonthlyDataPointsUnitsDisplay(state)('Searches');
          setTotalUsagesDisplay(state)(totalUsages + ' Searches');
        } else if (serviceType === SubscriptionServiceType.Translation) {
          setDailyDataPointsUnitsDisplay(state)('Characters');
          setMonthlyDataPointsUnitsDisplay(state)('Characters');
          setTotalUsagesDisplay(state)(totalUsages + ' Characters');
        } else if (serviceType === SubscriptionServiceType.Transcription) {
          setDailyDataPointsUnitsDisplay(state)(transcriptionDailyLabel);
          setMonthlyDataPointsUnitsDisplay(state)(transcriptionMonthlyLabel);
          setTotalUsagesDisplay(state)(formatTotalUsage(totalUsages));
        } else if (serviceType === SubscriptionServiceType.GenAI) {
          setDailyDataPointsUnitsDisplay(state)('Tokens');
          setMonthlyDataPointsUnitsDisplay(state)('Tokens');
          setTotalUsagesDisplay(state)(totalUsages + ' Tokens');
        } else {
          setDailyDataPointsUnitsDisplay(state)('Lookups');
          setMonthlyDataPointsUnitsDisplay(state)('Lookups');
          setTotalUsagesDisplay(state)(totalUsages + ' Lookups');
        }
      }
    }
  };
}

export function createState (): ITransactionsState {
  validationStore.clear(filterValidationModel);
  return reactive({
    subscriptionId: null,
    dailyUsages: [],
    monthlyUsages: [],
    totalUsages: 0,
    totalUsagesDisplay: '',
    dailyDataPointsUnitsDisplay: '',
    monthlyDataPointsUnitsDisplay: '',
    filterModel: {
      from: null,
      to: null,
      serviceType: null
    },
    transcriptionDailyDividend: undefined,
    transcriptionMonthlyDividend: undefined
  });
}

export function createForState (state: ITransactionsState): ITransactionsStore {
  return {
    state: readonly(state),
    setSubscriptionId: setSubscriptionId(state),
    setUsages: setUsages(state),
    setServiceType: setServiceType(state),
    setFrom: setFrom(state),
    setTo: setTo(state),
    loadUsages: loadUsages(state),
    setTotalUsagesDisplay: setTotalUsagesDisplay(state),
    setDailyDataPointsUnitsDisplay: setDailyDataPointsUnitsDisplay(state),
    setMonthlyDataPointsUnitsDisplay: setMonthlyDataPointsUnitsDisplay(state),
    setTransriptionDailyDividend: setTranscriptionDailyDividend(state),
    setTransriptionMonthlyDividend: setTranscriptionMonthlyDividend(state)
  };
}

export function provideStore (): ITransactionsStore {
  const state = createState();
  provide(TransactionsStoreKey, state);
  return createForState(state);
}

export function useStore (): ITransactionsStore {
  const state = inject<ITransactionsState>(TransactionsStoreKey);
  if (state === undefined) {
    throw new Error('Using TransactionStore before providing it!');
  }
  return createForState(state);
}
